import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The organization picker — the top bar's first control (plan-014 A4, FR12-AUTH-10; design §27.15.1
 * "App shell"; the prototype's `org` block in `design/WeTalk.dc.html`).
 *
 * It lists every organization the person can enter — from the auth_session's FRESH access listing,
 * never a remembered one — each with the person's role and the organization's state ("Owner · 3
 * workspaces", "Finance · no workspaces", "Suspended"), the current one ticked; then "Create an
 * organization" and "Organization settings". Picking one switches to it (`useSwitchScope`), into the
 * workspace the person last used there if it is still open to them, else its first enterable one,
 * else the organization alone.
 *
 * An organization with nowhere the person may go (closed; suspended for someone who cannot settle it;
 * no enterable workspace and no organization role) is listed, disabled, with the reason. While a WeTalk
 * operator acts for the person, the other organizations are disabled too: impersonation never
 * switches organization (design §27.5.5; the API refuses it as well).
 *
 * `OrganizationChooser` is the same list as a page: what the shell shows a person signed in with no
 * organization chosen (several to choose from, or none yet).
 */
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import type { AccountRole } from "@wetalk/domain";
import type { ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import { usePopover } from "./popover.js";
import { unreachableReasonOf } from "../scope/target.js";
import type { AccountChoiceView, AccountStateView } from "../api/gateway.js";

const ACCOUNT_ROLE_WORD: Readonly<Record<AccountRole, string>> = {
  get owner() { return portalText("shell", "owner_89ff3122"); },
  get admin() { return portalText("shell", "admin_4e7afebc"); },
  get finance() { return portalText("shell", "finance_1b48d3f0"); },
  get viewer() { return portalText("shell", "viewer_dbad9f12"); },
};

const STATE_WORD: Readonly<Record<AccountStateView, string | null>> = {
  get trial() { return portalText("shell", "trial_5f7537cf"); },
  active: null,
  get past_due() { return portalText("shell", "payment_overdue_964695bd"); },
  get suspended() { return portalText("shell", "suspended_794696a7"); },
  get closed() { return portalText("shell", "closed_88d86b77"); },
  // plan-014 B4: the organization's closure is under way.
  get closing() { return portalText("shell", "closing_76a032e9"); },
};

/** "Owner · 3 workspaces", "Finance · no workspaces", "2 workspaces · Suspended" — the picker's meta line. */
export function accountChoiceMetaOf(choice: AccountChoiceView): string {
  const enterable = choice.workspace.filter((one) => one.enterable).length;
  const workspaceWord =
    enterable === 0 ? portalText("shell", "no_workspaces_317b36eb") : enterable === 1 ? portalText("shell", "1_workspace_0676f207") : portalText("shell", "value_workspaces_b3a24e0a", { v0: String(String(enterable)) });
  const part = [
    choice.accountRole === null ? null : ACCOUNT_ROLE_WORD[choice.accountRole],
    workspaceWord,
    STATE_WORD[choice.state],
  ].filter((one): one is string => one !== null);
  return part.join(" · ");
}

export interface OrganizationPickerProps {
  /** The organization the console is in. */
  readonly current: AccountChoiceView;
  readonly choice: readonly AccountChoiceView[];
  readonly onPick: (choice: AccountChoiceView) => void;
  /** True while a switch is on its way: the items are disabled so it is not asked for twice. */
  readonly pending: boolean;
  /** A WeTalk operator is acting for the person: only the current organization may be used. */
  readonly impersonated: boolean;
  /** Whether "Organization settings" leads anywhere for this person (an organization role). */
  readonly settingsOpen: boolean;
  /**
   * 2026-10-14: the organizations this person closed that can still be reopened (`ClosedOrganizationMenu`), drawn
   * under the ones they can enter; `null` draws nothing.
   */
  readonly closed?: ReactNode;
}

function refusalOf(choice: AccountChoiceView, current: AccountChoiceView, impersonated: boolean): string | null {
  if (choice.accountId === current.accountId) return null;
  if (impersonated) return portalText("shell", "a_wetalk_operator_acting_for_you_cannot_switch_organizatio_fbdc6aea");
  return unreachableReasonOf(choice);
}

export function OrganizationPicker({
  current,
  choice,
  onPick,
  pending,
  impersonated,
  settingsOpen,
  closed = null,
}: OrganizationPickerProps): ReactNode {
  usePortalTranslation("shell");
  const { open, anchorRef, triggerRef, toggle, close } = usePopover();

  return (
    <div className="c-pop-anchor" ref={anchorRef}>
      <button
        type="button"
        ref={triggerRef}
        className="c-org-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={portalText("shell", "organization_value_ab571871", { v0: String(current.name) })}
        title={current.name}
        onClick={toggle}
      >
        <span className="c-org-mark">{current.initials}</span>
        <span className="c-org-name">{current.name}</span>
        <span className="c-org-caret">
          <Icon glyph={GLYPH.caretDown} size="xs" tone="muted" />
        </span>
      </button>

      {open ? (
        <div className="c-pop c-pop-left c-org-pop" role="menu" aria-label={portalText("shell", "switch_organization_a778bf12")}>
          <p className="c-pop-head">{portalText("shell", "organizations_07605242")}</p>
          {choice.map((one) => {
            const refusal = refusalOf(one, current, impersonated);
            const isCurrent = one.accountId === current.accountId;
            return (
              <button
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                key={one.accountId}
                className="c-pop-item"
                disabled={pending || refusal !== null}
                title={refusal ?? one.name}
                onClick={() => {
                  if (!isCurrent) onPick(one);
                  close();
                }}
              >
                <span className="c-mark c-mark-round">{one.initials}</span>
                <span className="c-pop-item-body">
                  <span className="c-pop-item-title c-ws-pop-name">{one.name}</span>
                  <span className="c-pop-item-dek">{accountChoiceMetaOf(one)}</span>
                  {refusal === null ? null : <span className="c-pop-item-dek">{refusal}</span>}
                </span>
                {isCurrent ? <Icon glyph={GLYPH.check} size="sm" tone="accent" label={portalText("shell", "current_organization_bbe94296")} /> : null}
              </button>
            );
          })}
          {closed}
          <div className="c-pop-rule" />
          <Link to="/start/organization" role="menuitem" className="c-pop-item" onClick={close}>
            <Icon glyph={GLYPH.plus} size="md" tone="accent" />
            <span className="c-pop-item-body">
              <span className="c-pop-item-title">{portalText("shell", "create_an_organization_b7310649")}</span>
            </span>
          </Link>
          {settingsOpen ? (
            <Link to="/organization" role="menuitem" className="c-pop-item" onClick={close}>
              <Icon glyph={GLYPH.buildings} size="md" tone="muted" />
              <span className="c-pop-item-body">
                <span className="c-pop-item-title">{portalText("shell", "organization_settings_89a2060b")}</span>
              </span>
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export interface OrganizationChooserProps {
  readonly choice: readonly AccountChoiceView[];
  readonly onPick: (choice: AccountChoiceView) => void;
  readonly pending: boolean;
  readonly problem: string | null;
}

/** The picker as a page — signed in, no organization chosen (FR12-AUTH-09's `selection_required`). */
export function OrganizationChooser({ choice, onPick, pending, problem }: OrganizationChooserProps): ReactNode {
  usePortalTranslation("shell");
  return (
    <section className="c-page c-page-narrow c-org-chooser" aria-labelledby="c-org-chooser-title">
      <p className="c-kicker">{choice.length === 0 ? portalText("shell", "welcome_ca4f9dcf") : portalText("shell", "organizations_07605242")}</p>
      <h1 id="c-org-chooser-title">{choice.length === 0 ? portalText("shell", "create_your_organization_271700cc") : portalText("shell", "choose_an_organization_406052d2")}</h1>
      <p className="c-org-chooser-lede">
        {choice.length === 0
          ? portalText("shell", "you_are_signed_in_but_you_do_not_belong_to_an_organization_d8966004")
          : portalText("shell", "you_belong_to_more_than_one_organization_choose_the_one_to_0f89f8d5")}
      </p>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
      {choice.length === 0 ? null : (
        <ul className="c-org-chooser-list">
          {choice.map((one) => {
            const refusal = unreachableReasonOf(one);
            return (
              <li key={one.accountId}>
                <button
                  type="button"
                  className="c-pop-item c-org-chooser-item"
                  disabled={pending || refusal !== null}
                  onClick={() => {
                    onPick(one);
                  }}
                >
                  <span className="c-mark c-mark-round">{one.initials}</span>
                  <span className="c-pop-item-body">
                    <span className="c-pop-item-title">{one.name}</span>
                    <span className="c-pop-item-dek">{accountChoiceMetaOf(one)}</span>
                    {refusal === null ? null : <span className="c-pop-item-dek">{refusal}</span>}
                  </span>
                  <Icon glyph={GLYPH.caretRight} size="sm" tone="muted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p>
        <Link to="/start/organization" className="btn btn-primary">
          <Icon glyph={GLYPH.plus} size="md" />
          <span>{portalText("shell", "create_an_organization_b7310649")}</span>
        </Link>
      </p>
    </section>
  );
}
